import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";

import { useNotification } from "../../contexts/NotificationContext";
import { Syringe } from "lucide-react";
import { getApiUrl } from "../../config/api";

export default function Vaccinations() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    vaccine_type: "",
    administered_date: "",
    veterinarian: "",
    notes: "",
    amount: ""
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/vaccinations`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/vaccinations`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.vaccine_type.trim()) {
      showWarning("Vaccine name is required");
      return;
    }
    if (!form.administered_date) {
      showWarning("Administration date is required");
      return;
    }

    try {
      // Ensure amount defaults to 0 if empty
      const formData = {
        ...form,
        amount: form.amount || "0.00"
      };
      
      await axios.post(getApiUrl(`/api/horses/${horseId}/vaccinations`), formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setForm({ vaccine_type: "", administered_date: "", veterinarian: "", notes: "", amount: "" });
      showSuccess("Vaccination record added successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Submit error:", err);
      showError("Failed to add vaccination record");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    vaccine_type: v.vaccine_type || "",
    administered_date: v.administered_date ? v.administered_date.split("T")[0] : "",
    veterinarian: v.veterinarian || "",
    notes: v.notes || "",
  });
};
  const saveEdit = async (id) => {
    // Validate required fields
    if (!editData.vaccine_type?.trim()) {
      showWarning("Vaccine name is required");
      return;
    }
    if (!editData.administered_date) {
      showWarning("Administration date is required");
      return;
    }

    try {
      // Ensure amount defaults to 0 if empty
      const editFormData = {
        ...editData,
        amount: editData.amount || "0.00"
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/vaccinations/${id}`), editFormData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setEditingId(null);
      showSuccess("Vaccination record updated successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Update error:", err);
      showError("Failed to update vaccination record");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/vaccinations/${recordId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      showSuccess("Vaccination record deleted successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete vaccination record");
    }
  };

  const goBack = () => navigate("/horses");

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button onClick={goBack} className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm">
        ← Back to Horses
      </button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Syringe className="mr-2 text-blue-600" /> Vaccination Records
      </h2>

      {!editingId && (
        <div className="bg-white shadow p-6 rounded mb-8">
          <h3 className="text-lg font-semibold mb-4">Add Vaccination</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vaccine Name *</label>
            <input
              name="vaccine_type"
              placeholder="e.g., West Nile, EEE/WEE, Rabies"
              value={form.vaccine_type}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Administration Date *</label>
            <input
              type="date"
              name="administered_date"
              value={form.administered_date}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
              required
            />
            <p className="text-xs text-gray-500 mt-1">When the vaccine was given</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Veterinarian</label>
            <input
              name="veterinarian"
              placeholder="e.g., Dr. Smith, ABC Veterinary Clinic"
              value={form.veterinarian}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
            <CurrencyInput
              name="amount"
              value={form.amount}
              onChange={(val) => setForm({ ...form, amount: val })}
              locale={navigator.language || 'en-US'}
              currency="USD"
              className="border px-3 py-2 rounded w-full"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Vaccination cost</p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input
              name="notes"
              placeholder="Any additional information about the vaccination"
              value={form.notes}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
            />
          </div>
          </div>
          <button
            onClick={handleSubmit}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Save Vaccination
          </button>
        </div>
      )}

      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vaccine Type *</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="vaccine_type"
                      value={editData.vaccine_type}
                      onChange={(e) => setEditData({ ...editData, vaccine_type: e.target.value })}
                      placeholder="e.g., Tetanus, West Nile, EEE/WEE, Rabies"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Administered Date *</label>
                    <input
                      type="date"
                      className="border px-2 py-1 rounded w-full"
                      name="administered_date"
                      value={editData.administered_date}
                      onChange={(e) => setEditData({ ...editData, administered_date: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Veterinarian</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="veterinarian"
                      value={editData.veterinarian}
                      onChange={(e) => setEditData({ ...editData, veterinarian: e.target.value })}
                      placeholder="Name of the veterinarian who administered the vaccine"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="notes"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Any additional information about the vaccination"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="amount"
                      value={editData.amount}
                      onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-300 text-black px-4 py-1 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{rec.vaccine_type}</p>
                    <p className="text-sm text-gray-600">
                      {formatWithSystemLocale(rec.administered_date)} by {rec.veterinarian}
                    </p>
                    {rec.notes && <p className="text-sm text-gray-500 italic">{rec.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
